import {useTaillightsController} from '@github-ui/taillights/provider'
import type {SourceRange, SourceRect} from '@github-ui/taillights-worker/document'
import {startTransition, useEffect, useMemo, useState} from 'react'

type RectangleLayers = {
  symbolRects: readonly SourceRect[]
  controlledRects: readonly SourceRect[]
  focusedRects: readonly SourceRect[]
}

type RangeRequest = {
  collapsed: readonly number[]
  blockId?: string
  enabled?: boolean
  symbolRanges?: readonly SourceRange[]
  highlightRanges?: readonly SourceRange[]
  focusedHighlightRange?: SourceRange | null
  onRenderError?: (error: Error) => void
}

const EMPTY_RANGES: readonly SourceRange[] = []
const EMPTY_LAYERS: RectangleLayers = {symbolRects: [], controlledRects: [], focusedRects: []}

/** Requests worker geometry for passive highlight layers and ignores results from outdated configurations. */
export function useRangeDecorations({
  collapsed,
  blockId,
  enabled = true,
  symbolRanges = EMPTY_RANGES,
  highlightRanges = EMPTY_RANGES,
  focusedHighlightRange,
  onRenderError,
}: RangeRequest): RectangleLayers {
  const controller = useTaillightsController()
  const request = useMemo(
    () => ({controller, collapsed, blockId, enabled, symbolRanges, highlightRanges, focusedHighlightRange}),
    [controller, collapsed, blockId, enabled, symbolRanges, highlightRanges, focusedHighlightRange],
  )
  const [result, setResult] = useState<{request: typeof request; rectangles: RectangleLayers}>()

  useEffect(() => {
    if (!controller || !enabled || (!symbolRanges.length && !highlightRanges.length && !focusedHighlightRange)) return
    let active = true
    const rects = (ranges: readonly SourceRange[]) =>
      ranges.length ? controller.rects({ranges, collapsed, blockId}) : Promise.resolve([])

    void (async () => {
      try {
        const [symbolRects, controlledRects, focusedRects] = await Promise.all([
          rects(symbolRanges),
          rects(highlightRanges),
          rects(focusedHighlightRange ? [focusedHighlightRange] : EMPTY_RANGES),
        ])
        if (active) {
          startTransition(() => setResult({request, rectangles: {symbolRects, controlledRects, focusedRects}}))
        }
      } catch (error) {
        if (!active) return
        const failure = error instanceof Error ? error : new Error(String(error))
        if (onRenderError) onRenderError(failure)
        else reportError(failure)
      }
    })()
    return () => {
      active = false
    }
  }, [
    controller,
    collapsed,
    blockId,
    enabled,
    symbolRanges,
    highlightRanges,
    focusedHighlightRange,
    request,
    onRenderError,
  ])

  return result?.request === request ? result.rectangles : EMPTY_LAYERS
}
